/**
 * /swot-analysis-template — SEO template page for "swot template" /
 * "swot analysis template" (~132 traffic, vol 2k, medium-strong fit).
 * Hands over the SWOT 2x2 + the questions per quadrant, then the Draftlize
 * turn: a SWOT is a snapshot of a moving competitive field, and a static
 * grid is wrong the moment a rival ships or a strength erodes.
 *
 * English-only + inline. Chinese variant in SwotAnalysisTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function SwotAnalysisTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/swot-analysis-template"
      documentTitle="SWOT Analysis Template (That Stays Current) · Draftlize"
      documentDescription="A SWOT analysis template you can copy — Strengths, Weaknesses, Opportunities, Threats, with the questions to ask in each — plus how to keep it from going stale the moment a rival moves."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · SWOT analysis</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A SWOT analysis template<br/>
            <em>that doesn&apos;t freeze in time.</em>
          </h1>
          <p className="lede">
            You came for a <em>SWOT template</em> — the four quadrants, the prompts that fill each one. It&apos;s below; copy it into a doc or a slide. But know the failure mode: a SWOT is a snapshot of a competitive field that won&apos;t hold still. The grid is accurate the afternoon you make it and wrong the week a rival ships, a strength gets commoditized, or a threat materializes — and a static SWOT has no idea any of that happened. Draftlize gives you the same four quadrants, tied to the positioning decisions they should move.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Four quadrants. <em>Copy them anywhere.</em></h2>
          </div>
          <p>SWOT sorts factors two ways: internal vs external, and helpful vs harmful. That&apos;s the whole framework. The prompts below are what to actually ask in each quadrant — the difference between a useful SWOT and four lists of platitudes.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Strengths', hint: 'Internal · Helpful', body: 'What you do better than alternatives, that you control — a distribution advantage, proprietary data, a team capability, a brand. Ask: what do we win on today, and is the advantage durable or about to be competed away? Vague strengths ("great team") are useless; name the specific edge.' },
            { k: 'Weaknesses', hint: 'Internal · Harmful', body: 'Where you\'re behind or exposed, that you control — a gap in the product, a thin team, technical debt, no enterprise story. Ask: what do we lose deals on, and what would a competitor attack first? Honesty here is the whole value; a flattering SWOT is a waste of an afternoon.' },
            { k: 'Opportunities', hint: 'External · Helpful', body: 'Shifts in the world you could exploit, that you don\'t control — a new platform, a behavior change, a competitor stumbling, a regulation. Ask: what just changed that we could ride? An opportunity is external by definition; "we could build feature X" is a plan, not an opportunity.' },
            { k: 'Threats', hint: 'External · Harmful', body: 'Forces that could hurt you, that you don\'t control — a well-funded entrant, a platform changing terms, a shift in demand. Ask: what could make our strengths irrelevant? Threats are where a SWOT earns its keep, and exactly the quadrant that goes stale fastest when a rival moves.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '230px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          The point isn&apos;t the grid — it&apos;s the move it implies: use strengths on opportunities, shore up weaknesses against threats. See also the <a href="/competitive-analysis-template" style={{ color: 'var(--amber)' }}>competitive analysis template</a>.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a SWOT <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It&apos;s a snapshot of a moving field</h3>
            <p>A SWOT captures the competitive landscape at one moment. But threats and opportunities are external and constantly moving — a rival ships, a platform shifts, demand turns. The grid doesn&apos;t move with them, so within weeks a real fraction of it is quietly false, and you can&apos;t tell which fraction.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">It&apos;s disconnected from the decisions it should drive</h3>
            <p>The point of a SWOT is to inform positioning and strategy. But the analysis lives in one doc and the <a href="/product-strategy" style={{ color: 'var(--amber)' }}>strategy</a> in another, so when a threat changes nothing walks forward to the strategy that assumed it away. The SWOT becomes a workshop artifact, not a live input.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Nobody re-runs it on a trigger</h3>
            <p>SWOTs get redone on a calendar — annually, at planning — not when the thing that invalidated them happened. So the version you&apos;re acting on is the one from the last offsite, describing a competitive field that has since moved on without telling you.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same grid, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each factor becomes a card</h3>
            <p>In Draftlize a strength, weakness, opportunity, or threat is a card — a claim with the evidence behind it — not a bullet in a grid. A competitor threat links to the observation that established it, so &quot;is this still true&quot; has an answer you can check.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A factor changes, dependents flag stale</h3>
            <p>A rival ships the thing that was your strength. You update that card, and every positioning decision and strategy bet that leaned on it auto-flags stale — the way a build system invalidates everything downstream of a changed file. The SWOT drives the strategy instead of decorating a deck.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent reads the current landscape</h3>
            <p>When an AI agent helps with positioning or the next competitive move over MCP, it reads the live SWOT factors — not last year&apos;s grid — so its suggestions account for the rival that shipped last month, not the one that existed at the last planning session.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A SWOT is true the afternoon you make it. The competitive field it describes doesn&apos;t hold that pose.
        </blockquote>
        <cite>Keep the four quadrants. Tie each factor to the decisions it should move.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What are the four parts of a SWOT analysis?', a: 'Strengths, Weaknesses, Opportunities, and Threats. Strengths and weaknesses are internal and within your control; opportunities and threats are external and outside it. The grid sorts factors by internal vs external and helpful vs harmful.' },
            { q: 'What is the difference between SWOT and a competitive analysis?', a: 'A SWOT is a high-level snapshot of your own position across four quadrants. A competitive analysis is a deeper, structured comparison against specific rivals on specific dimensions. A SWOT is quick orientation; competitive analysis is the detailed homework that feeds it.' },
            { q: 'How do you actually use a SWOT (not just fill it in)?', a: 'The value is in the moves it implies: use strengths to capture opportunities, and shore up weaknesses against threats. A SWOT that produces four lists and no decisions was a waste of time — each factor should change a positioning or strategy choice.' },
            { q: 'How often should a SWOT be updated?', a: 'When a factor changes, not on a fixed calendar. Threats and opportunities are external and move constantly, so a SWOT redone only at annual planning is usually describing a competitive field that has already shifted.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep your SWOT current <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Fill the four quadrants above, or build them as cards in Draftlize linked to your positioning decisions — so when a rival moves, the affected strengths, threats, and the strategy that rested on them all flag stale.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
